/**
 * TitleRow
 *
 * Editable title row for automation cards.
 * Desktop: ✏️ Name [ActionType badge] ... [💾] [⋯]
 * Mobile: Stacks vertically with action buttons on separate row
 */

import {
  faClockRotateLeft,
  faClone,
  faEllipsis,
  faPencil,
  faSave,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Chip } from 'src/components/Chip';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { VersionHistory } from 'src/shared/VersionHistory';
import { Identifier } from 'src/types/utilityTypes';
import { formatFullDateAndTime } from 'src/utils/dateFormatter';

export interface TitleRowProps {
  name: string | null;
  actionLabel: string;
  automationId?: Identifier;
  createdAt?: number | null;
  updatedAt?: number | null;
  editing: boolean;
  onEditStart: () => void;
  onEditEnd: () => void;
  onNameChange: (name: string) => void;
  onRemove?: () => void;
  onClone?: () => void;
  onSave?: () => void;
}

export const TitleRow: FC<TitleRowProps> = ({
  name,
  actionLabel,
  automationId,
  createdAt,
  updatedAt,
  editing,
  onEditStart,
  onEditEnd,
  onNameChange,
  onRemove,
  onClone,
  onSave,
}) => {
  const { t } = useTranslation();
  const [historyOpen, setHistoryOpen] = useState(false);

  if (editing) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <TextField
          value={name || ''}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder={t('automations.common.enterName')}
          size="small"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') onEditEnd();
          }}
        />
        <Typography
          variant="body2"
          color="primary"
          sx={{ cursor: 'pointer' }}
          onClick={onEditEnd}
        >
          {t('automations.common.done')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      gap={1}
    >
      {/* Name and Action Label */}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" flex={1}>
        <Box display="flex" alignItems="center">
        <Typography variant="h6">{name || t('automations.common.automation')}</Typography>
          <IconButton size="small" onClick={onEditStart}>
            <Icon icon={faPencil} size="small" />
          </IconButton>
        </Box>
        <Tooltip
          title={
            createdAt && !!automationId ? (
              <>
                {t('automations.common.createdOn', { date: formatFullDateAndTime(createdAt) })}
                {updatedAt && (
                  <>
                    <br />
                    {t('automations.common.updatedOn', { date: formatFullDateAndTime(updatedAt) })}
                  </>
                )}
              </>
            ) : (
              ''
            )
          }
        >
          <Chip label={actionLabel} size="small" color="primary" />
        </Tooltip>
        {!automationId && (
          <Typography variant="caption" color="error">{t('automations.common.saveToCreateAutomation')}</Typography>
        )}
      </Box>

      {/* Action buttons */}
      <Box display="flex" alignItems="center" gap={0.5}>
        {onSave && (
          <IconButton onClick={onSave} size="small" color="success">
            <Icon icon={faSave} size="small" />
          </IconButton>
        )}
        <DropdownMenu
          trigger={
            <IconButton size="small">
              <Icon icon={faEllipsis} size="small" />
            </IconButton>
          }
        >
          <Stack>
            {!!automationId && (
              <ListItem
                icon={faClockRotateLeft}
                primary={t('automations.common.viewHistory')}
                onClick={() => setHistoryOpen(true)}
              />
            )}
            {onClone && (
              <ListItem
                icon={faClone}
                primary={t('automations.common.clone')}
                onClick={onClone}
              />
            )}
            {onRemove && (
              <ListItem
                icon={faTrash}
                primary={t('automations.common.delete')}
                onClick={onRemove}
              />
            )}
          </Stack>
        </DropdownMenu>
      </Box>

      {/* Version History Dialog */}
      {!!automationId && (
        <VersionHistory
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          itemType="Reminder"
          itemId={automationId}
        />
      )}
    </Box>
  );
};

